import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  XCircle,
  Clock,
  ChevronDown,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Filter,
  Zap,
} from 'lucide-react';

import { TraceCard, shortEntry } from './execution-feed';

import type { ExecutionSummaryV2 } from '@/lib/types';
import { getExecutionTimestamp, getExecutionDuration } from '@/lib/execution-helpers';
import { cn } from '@/lib/utils';
import { useExecutionStore } from '@/lib/storage/provider';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ActiveFilters {
  keyword: string;
  status: '' | 'error' | 'success';
  projectId: string;
  functionName: string;
  errorType: string;
  minDuration: number;
  maxDuration: number;
  afterMs: number;
  beforeMs: number;
  sort: 'time' | 'error' | 'latency';
}

const EMPTY: ActiveFilters = {
  keyword: '',
  status: '',
  projectId: '',
  functionName: '',
  errorType: '',
  minDuration: 0,
  maxDuration: 0,
  afterMs: 0,
  beforeMs: 0,
  sort: 'error',
};

const TIME_PRESETS = [
  { label: 'Last 5 minutes', afterMs: (): number => Date.now() - 5 * 60_000 },
  { label: 'Last 30 minutes', afterMs: (): number => Date.now() - 30 * 60_000 },
  { label: 'Last 1 hour', afterMs: (): number => Date.now() - 60 * 60_000 },
  { label: 'Last 6 hours', afterMs: (): number => Date.now() - 6 * 3600_000 },
  {
    label: 'Today',
    afterMs: (): number => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    },
  },
];

const DURATION_PRESETS = [
  { label: 'Any duration', min: 0, max: 0 },
  { label: 'Fast  (<100ms)', min: 0, max: 100 },
  { label: 'Normal (100–500ms)', min: 100, max: 500 },
  { label: 'Slow  (500ms–1s)', min: 500, max: 1000 },
  { label: 'Very slow  (>1s)', min: 1000, max: 0 },
  { label: 'Critical  (>3s)', min: 3000, max: 0 },
];

// ─── Dropdown wrapper ────────────────────────────────────────────────────────

function FilterDropdown({
  label,
  active,
  children,
  icon,
}: {
  label: string;
  active?: boolean | undefined;
  children: React.ReactNode;
  icon?: React.ReactNode;
}): import('react/jsx-runtime').JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return (): void => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        onClick={(): void => setOpen((v): boolean => !v)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-[11px] font-medium transition-all h-9',
          (active ?? false)
            ? 'bg-blue-500/15 border-blue-500/30 text-blue-300'
            : 'bg-[#0d1117] border-white/10 text-muted-foreground/70 hover:border-white/20 hover:text-foreground',
        )}
      >
        {icon}
        <span>{label}</span>
        <ChevronDown
          className={cn(
            'h-3 w-3 transition-transform text-muted-foreground/40',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1.5 min-w-[200px] bg-[#0d1117] border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.7)] z-50 overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownItem({
  label,
  sublabel,
  active,
  onClick,
  colorClass,
}: {
  label: string;
  sublabel?: string | undefined;
  active?: boolean | undefined;
  onClick: () => void;
  colorClass?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between px-3.5 py-2.5 text-left text-[12px] transition-colors',
        (active ?? false) ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]',
      )}
    >
      <span className={cn('font-medium', colorClass ?? 'text-foreground/80')}>{label}</span>
      <div className="flex items-center gap-1.5">
        {sublabel != null && (
          <span className="text-[10px] font-mono text-muted-foreground/40">{sublabel}</span>
        )}
        {(active ?? false) && <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
      </div>
    </button>
  );
}

function DropdownDivider({ label }: { label: string }): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="px-3.5 pt-2.5 pb-1">
      <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest">
        {label}
      </span>
    </div>
  );
}

// ─── Status Selector (visual button group) ────────────────────────────────────

function StatusSelector({
  value,
  onChange,
}: {
  value: '' | 'error' | 'success';
  onChange: (v: '' | 'error' | 'success') => void;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="flex items-center bg-[#0d1117] border border-white/10 rounded-lg p-0.5 h-9">
      {(
        [
          { v: '', label: 'All' },
          { v: 'error', label: '❌ Errors' },
          { v: 'success', label: '✅ Success' },
        ] as const
      ).map((opt): import('react/jsx-runtime').JSX.Element => (
        <button
          key={opt.v}
          onClick={(): void => onChange(opt.v)}
          className={cn(
            'px-3 py-1 rounded-md text-[11px] font-medium transition-all',
            value === opt.v
              ? opt.v === 'error'
                ? 'bg-[var(--flux-error)]/15 text-[var(--flux-error)] border border-[var(--flux-error)]/20'
                : opt.v === 'success'
                  ? 'bg-[var(--flux-success)]/15 text-[var(--flux-success)] border border-[var(--flux-success)]/20'
                  : 'bg-white/8 text-foreground border border-white/10'
              : 'text-muted-foreground/50 hover:text-muted-foreground border border-transparent',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function SortSelector({
  value,
  onChange,
}: {
  value: 'time' | 'error' | 'latency';
  onChange: (v: 'time' | 'error' | 'latency') => void;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="flex items-center bg-[#0d1117] border border-white/10 rounded-lg p-0.5 h-9">
      {(
        [
          { v: 'error', label: 'Criticality' },
          { v: 'time', label: 'Newest' },
          { v: 'latency', label: 'Latency' },
        ] as const
      ).map((opt): import('react/jsx-runtime').JSX.Element => (
        <button
          key={opt.v}
          onClick={(): void => onChange(opt.v)}
          className={cn(
            'px-3 py-1 rounded-md text-[11px] font-medium transition-all',
            value === opt.v
              ? 'bg-white/8 text-foreground border border-white/10'
              : 'text-muted-foreground/50 hover:text-muted-foreground border border-transparent',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Active filter pill ───────────────────────────────────────────────────────

function FilterPill({
  label,
  onRemove,
  colorClass,
}: {
  label: string;
  onRemove: () => void;
  colorClass?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 pl-2.5 pr-1.5 py-0.5 rounded-full border text-[10px] font-mono',
        colorClass ?? 'bg-white/5 border-white/10 text-foreground/60',
      )}
    >
      {label}
      <button onClick={onRemove} className="hover:text-white transition-colors ml-0.5">
        <XCircle className="h-3 w-3" />
      </button>
    </span>
  );
}

// ─── Intelligence Banner ─────────────────────────────────────────────────────

function IntelligenceBanner({
  executions,
}: {
  executions: ExecutionSummaryV2[];
}): import('react/jsx-runtime').JSX.Element | null {
  if (executions.length === 0) return null;
  const errors = executions.filter((e): boolean => e.status === 'error');
  const avgMs = Math.round(
    executions.reduce((s, e): number => s + getExecutionDuration(e), 0) / executions.length,
  );
  const errorRate = errors.length / executions.length;

  if (errorRate > 0.6) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[var(--flux-error)]/[0.06] border border-[var(--flux-error)]/20 rounded-xl text-[11px] font-mono text-[var(--flux-error)]">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span>
          <span className="font-bold">
            {errors.length} of {executions.length}
          </span>{' '}
          results are failing — likely service degradation. Check the error type breakdown.
        </span>
      </div>
    );
  }
  if (avgMs > 800) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[var(--flux-warning)]/[0.06] border border-[var(--flux-warning)]/20 rounded-xl text-[11px] font-mono text-[var(--flux-warning)]">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        <span>
          High latency detected — avg <span className="font-bold">{avgMs}ms</span> across these
          results. Check your slowest function.
        </span>
      </div>
    );
  }
  if (errors.length === 0 && executions.length > 0) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[var(--flux-success)]/[0.04] border border-[var(--flux-success)]/15 rounded-xl text-[11px] font-mono text-[var(--flux-success)]">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
        <span>
          All <span className="font-bold">{executions.length}</span> results are healthy.
        </span>
      </div>
    );
  }
  return null;
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({
  hasFilters,
  onClear,
}: {
  hasFilters: boolean;
  onClear: () => void;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="py-24 flex flex-col items-center justify-center text-center border border-white/5 rounded-2xl bg-[#0d1117]/40">
      <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/8 flex items-center justify-center mb-5">
        <Search className="h-6 w-6 text-muted-foreground/20" />
      </div>
      <p className="text-base font-semibold text-foreground/40 mb-1">No executions match</p>
      <p className="text-xs text-muted-foreground/30 max-w-[240px]">
        {hasFilters ? 'Try loosening your filters.' : 'Run some traffic and it will appear here.'}
      </p>
      {hasFilters && (
        <button
          onClick={onClear}
          className="mt-5 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-[11px] font-mono text-blue-400 hover:bg-blue-500/15 transition-colors"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function ExecutionExplorer(): import('react/jsx-runtime').JSX.Element {
  const [searchParams] = useSearchParams();
  const { executions: allExecutions, isLoading: storeLoading } = useExecutionStore();

  const [filters, setFilters] = useState<ActiveFilters>(() => ({
    ...EMPTY,
    status: (searchParams.get('status') as '' | 'error' | 'success') || '',
    functionName: searchParams.get('functionName') ?? '',
  }));
  const [similarTo, setSimilarTo] = useState<ExecutionSummaryV2 | null>(null);

  // Derive facets from allExecutions locally
  const facets = useMemo(() => {
    const projects: Record<
      string,
      { id: string; name: string; count: number; errorCount: number }
    > = {};
    const functions: Record<
      string,
      { name: string; shortName: string; count: number; errorCount: number }
    > = {};
    const errorTypes: Record<string, { label: string; count: number }> = {};

    for (const e of allExecutions) {
      const projectId = e.projectId ?? 'unknown';
      const proj = (projects[projectId] ??= {
        id: projectId,
        name: projectId,
        count: 0,
        errorCount: 0,
      });
      proj.count++;
      if (e.status === 'error') proj.errorCount++;

      const fnKey = e.entry ?? 'unknown';
      const fn = (functions[fnKey] ??= {
        name: fnKey,
        shortName: shortEntry(fnKey),
        count: 0,
        errorCount: 0,
      });
      fn.count++;
      if (e.status === 'error') fn.errorCount++;

      if (e.status === 'error' && e.error != null) {
        const et = e.error.name ?? 'Error';
        (errorTypes[et] ??= { label: et, count: 0 }).count++;
      }
    }

    return {
      projects: Object.values(projects).sort((a, b): number => b.count - a.count),
      functions: Object.values(functions).sort((a, b): number => b.count - a.count),
      errorTypes: Object.values(errorTypes).sort((a, b): number => b.count - a.count),
    };
  }, [allExecutions]);

  const setF = useCallback(<K extends keyof ActiveFilters>(key: K, val: ActiveFilters[K]): void => {
    setFilters((f) => ({ ...f, [key]: val }));
    setSimilarTo(null);
  }, []);

  const clearAll = (): void => {
    setFilters(EMPTY);
    setSimilarTo(null);
  };

  // Filter local executions
  const filteredExecutions = useMemo((): ExecutionSummaryV2[] => {
    let result = allExecutions;

    if (filters.keyword) {
      const q = filters.keyword.toLowerCase();
      result = result.filter(
        (e): boolean =>
          ((e.executionId ?? '').toLowerCase().includes(q) ||
            (e.entry?.toLowerCase().includes(q) ?? false) ||
            e.error?.message?.toLowerCase().includes(q)) ??
          false,
      );
    }

    if (filters.status) {
      result = result.filter((e): boolean => e.status === filters.status);
    }

    if (filters.projectId) {
      result = result.filter((e): boolean => e.projectId === filters.projectId);
    }

    if (filters.functionName) {
      result = result.filter((e): boolean => e.entry === filters.functionName);
    }

    if (filters.errorType) {
      result = result.filter(
        (e): boolean => e.status === 'error' && e.error?.name === filters.errorType,
      );
    }

    if (filters.minDuration > 0) {
      result = result.filter((e): boolean => getExecutionDuration(e) >= filters.minDuration);
    }
    if (filters.maxDuration > 0) {
      result = result.filter((e): boolean => getExecutionDuration(e) <= filters.maxDuration);
    }

    if (filters.afterMs > 0) {
      result = result.filter(
        (e): boolean => Date.parse(getExecutionTimestamp(e)) >= filters.afterMs,
      );
    }
    if (filters.beforeMs > 0) {
      result = result.filter(
        (e): boolean => Date.parse(getExecutionTimestamp(e)) <= filters.beforeMs,
      );
    }

    // Sort
    if (filters.sort === 'time') {
      result = [...result].sort((a, b): number => {
        const ta = Date.parse(getExecutionTimestamp(a));
        const tb = Date.parse(getExecutionTimestamp(b));
        return tb - ta;
      });
    } else if (filters.sort === 'latency') {
      result = [...result].sort(
        (a, b): number => getExecutionDuration(b) - getExecutionDuration(a),
      );
    } else if (filters.sort === 'error') {
      result = [...result].sort((a, b): 0 | 1 | -1 => {
        if (a.status === 'error' && b.status !== 'error') return -1;
        if (a.status !== 'error' && b.status === 'error') return 1;
        return 0;
      });
    }

    if (similarTo) {
      result = result.filter(
        (e): boolean =>
          e.entry === similarTo.entry &&
          e.status === similarTo.status &&
          e.executionId !== similarTo.executionId,
      );
    }

    return result;
  }, [allExecutions, filters, similarTo]);

  const isLoading = storeLoading;
  const isFetching = false;
  const executions = filteredExecutions;

  const hasFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY) || !!similarTo;
  const totalResults = allExecutions.length;
  const errorCount = executions.filter((e): boolean => e.status === 'error').length;
  const slowCount = executions.filter((e): boolean => getExecutionDuration(e) > 1000).length;

  // Derive human label for active time/duration filters
  const timeLabel = useMemo((): string | null => {
    if (!filters.afterMs) return null;
    const preset = TIME_PRESETS.find(
      (p): boolean => Math.abs(p.afterMs() - filters.afterMs) < 60000,
    ); // 1min tolerance
    if (preset) return preset.label;
    const from = new Date(filters.afterMs).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    const to = filters.beforeMs
      ? new Date(filters.beforeMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : 'now';
    return `${from} → ${to}`;
  }, [filters.afterMs, filters.beforeMs]);

  const durationLabel = useMemo((): string | null => {
    if (!filters.minDuration && !filters.maxDuration) return null;
    return (
      DURATION_PRESETS.find(
        (p): boolean => p.min === filters.minDuration && p.max === filters.maxDuration,
      )?.label ?? `≥${filters.minDuration}ms`
    );
  }, [filters.minDuration, filters.maxDuration]);

  const functionLabel = filters.functionName ? shortEntry(filters.functionName) : null;
  const errorTypeLabel = filters.errorType || null;
  const projectLabel =
    facets.projects.find((p): boolean => p.id === filters.projectId)?.name ?? null;

  return (
    <div className="space-y-5">
      {/* ── 1. Keyword Search ─────────────────────────────────────── */}
      <div className="relative flex items-center gap-3 h-12 px-4 bg-[#0d1117] border border-white/10 rounded-xl hover:border-white/15 focus-within:border-blue-500/30 transition-all">
        <Search className="h-4 w-4 text-muted-foreground/40 shrink-0" />
        <input
          value={filters.keyword}
          onChange={(e): void => setF('keyword', e.target.value)}
          placeholder="Search by request ID, keyword, or function name…"
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/30 outline-none"
        />
        {filters.keyword && (
          <button
            onClick={(): void => setF('keyword', '')}
            className="text-muted-foreground/30 hover:text-muted-foreground"
          >
            <XCircle className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* ── 2. Visual Filters Row ─────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Sort — segmented button group */}
        <SortSelector value={filters.sort} onChange={(v): void => setF('sort', v)} />

        <div className="h-7 w-px bg-white/8 mx-0.5" />

        {/* Status — segmented button group */}
        <StatusSelector value={filters.status} onChange={(v): void => setF('status', v)} />

        <div className="h-7 w-px bg-white/8 mx-0.5" />

        {/* App (project) filter */}
        <FilterDropdown
          label={projectLabel ?? 'App'}
          active={!(projectLabel == null)}
          icon={<Filter className="h-3.5 w-3.5 text-muted-foreground/50" />}
        >
          <DropdownItem
            label="All apps"
            active={!filters.projectId}
            onClick={(): void => setF('projectId', '')}
          />
          {facets.projects.length > 0 && <DropdownDivider label="Your apps" />}
          {facets.projects.map((p): import('react/jsx-runtime').JSX.Element => (
            <DropdownItem
              key={p.id}
              label={p.name}
              sublabel={p.errorCount > 0 ? `${p.errorCount} errors` : `${p.count} calls`}
              active={filters.projectId === p.id}
              onClick={(): void => setF('projectId', filters.projectId === p.id ? '' : p.id)}
              colorClass={p.errorCount > 0 ? 'text-orange-400' : undefined}
            />
          ))}
        </FilterDropdown>

        {/* Function dropdown */}
        {facets.functions.length > 0 && (
          <FilterDropdown
            label={functionLabel ?? 'Function'}
            active={!(functionLabel == null)}
            icon={<Zap className="h-3.5 w-3.5 text-muted-foreground/50" />}
          >
            <DropdownItem
              label="Any function"
              active={!filters.functionName}
              onClick={(): void => setF('functionName', '')}
            />
            <DropdownDivider label="Your functions" />
            {facets.functions.map((fn): import('react/jsx-runtime').JSX.Element => (
              <DropdownItem
                key={fn.name}
                label={fn.shortName}
                sublabel={fn.errorCount > 0 ? `${fn.errorCount} errors` : `${fn.count} calls`}
                active={filters.functionName === fn.name}
                onClick={(): void =>
                  setF('functionName', filters.functionName === fn.name ? '' : fn.name)
                }
                colorClass={fn.errorCount > 0 ? 'text-orange-400' : undefined}
              />
            ))}
          </FilterDropdown>
        )}

        {/* Error Type dropdown */}
        {facets.errorTypes.length > 0 && (
          <FilterDropdown
            label={errorTypeLabel ?? 'Error Type'}
            active={!(errorTypeLabel == null)}
            icon={<AlertTriangle className="h-3.5 w-3.5 text-muted-foreground/50" />}
          >
            <DropdownItem
              label="Any error type"
              active={!filters.errorType}
              onClick={(): void => setF('errorType', '')}
            />
            <DropdownDivider label="Detected in your data" />
            {facets.errorTypes.map((et): import('react/jsx-runtime').JSX.Element => (
              <DropdownItem
                key={et.label}
                label={et.label}
                sublabel={`${et.count}×`}
                active={filters.errorType === et.label}
                onClick={(): void =>
                  setF('errorType', filters.errorType === et.label ? '' : et.label)
                }
                colorClass="text-[var(--flux-error)]"
              />
            ))}
          </FilterDropdown>
        )}

        {/* Time Range dropdown */}
        <FilterDropdown
          label={timeLabel ?? 'Time Range'}
          active={!(timeLabel == null)}
          icon={<Clock className="h-3.5 w-3.5 text-muted-foreground/50" />}
        >
          <DropdownItem
            label="Any time"
            active={!filters.afterMs}
            onClick={(): void => {
              setF('afterMs', 0);
              setF('beforeMs', 0);
            }}
          />
          <DropdownDivider label="Quick select" />
          {TIME_PRESETS.map((p): import('react/jsx-runtime').JSX.Element => (
            <DropdownItem
              key={p.label}
              label={p.label}
              active={Math.abs(filters.afterMs - p.afterMs()) < 60000}
              onClick={(): void => {
                setF('afterMs', p.afterMs());
                setF('beforeMs', 0);
              }}
            />
          ))}
          <DropdownDivider label="Custom window (today)" />
          <div className="px-3.5 pb-3 flex items-center gap-2">
            <div className="flex-1">
              <label className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-wide block mb-1">
                From
              </label>
              <input
                type="time"
                className="w-full bg-[#1a1b1e] border border-white/10 rounded-md px-2 py-1.5 text-[11px] font-mono text-foreground/80 outline-none focus:border-blue-500/30"
                onChange={(e): void => {
                  if (!e.target.value) return setF('afterMs', 0);
                  const [h, m] = e.target.value.split(':').map(Number);
                  const d = new Date();
                  d.setHours(h!, m, 0, 0);
                  setF('afterMs', d.getTime());
                }}
              />
            </div>
            <div className="flex-1">
              <label className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-wide block mb-1">
                To
              </label>
              <input
                type="time"
                className="w-full bg-[#1a1b1e] border border-white/10 rounded-md px-2 py-1.5 text-[11px] font-mono text-foreground/80 outline-none focus:border-blue-500/30"
                onChange={(e): void => {
                  if (!e.target.value) return setF('beforeMs', 0);
                  const [h, m] = e.target.value.split(':').map(Number);
                  const d = new Date();
                  d.setHours(h!, m, 59, 999);
                  setF('beforeMs', d.getTime());
                }}
              />
            </div>
          </div>
        </FilterDropdown>

        {/* Duration */}
        <FilterDropdown
          label={durationLabel ?? 'Duration'}
          active={!(durationLabel == null)}
          icon={<Activity className="h-3.5 w-3.5 text-muted-foreground/50" />}
        >
          {DURATION_PRESETS.map((p): import('react/jsx-runtime').JSX.Element => (
            <DropdownItem
              key={p.label}
              label={p.label}
              active={filters.minDuration === p.min && filters.maxDuration === p.max}
              onClick={(): void => {
                setF('minDuration', p.min);
                setF('maxDuration', p.max);
              }}
              colorClass={p.min >= 1000 ? 'text-[var(--flux-warning)]' : undefined}
            />
          ))}
        </FilterDropdown>

        {hasFilters && (
          <button
            onClick={clearAll}
            className="ml-auto text-[11px] font-mono text-muted-foreground/40 hover:text-[var(--flux-error)] flex items-center gap-1.5 transition-colors"
          >
            <XCircle className="h-3.5 w-3.5" /> Clear all
          </button>
        )}
      </div>

      {/* ── 3. Active filter pills ─────────────────────────────────── */}
      {hasFilters && (
        <div className="flex items-center gap-2 flex-wrap -mt-1">
          {filters.status && (
            <FilterPill
              label={filters.status === 'error' ? '❌ Errors only' : '✅ Success only'}
              onRemove={(): void => setF('status', '')}
              colorClass={
                filters.status === 'error'
                  ? 'bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20 text-[var(--flux-error)]'
                  : 'bg-[var(--flux-success)]/10 border-[var(--flux-success)]/20 text-[var(--flux-success)]'
              }
            />
          )}
          {functionLabel != null && (
            <FilterPill
              label={`fn: ${functionLabel}`}
              onRemove={(): void => setF('functionName', '')}
            />
          )}
          {errorTypeLabel != null && (
            <FilterPill
              label={`error: ${errorTypeLabel}`}
              onRemove={(): void => setF('errorType', '')}
              colorClass="bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20 text-[var(--flux-error)]/80"
            />
          )}
          {timeLabel != null && (
            <FilterPill
              label={`⏱ ${timeLabel}`}
              onRemove={(): void => {
                setF('afterMs', 0);
                setF('beforeMs', 0);
              }}
              colorClass="bg-blue-500/10 border-blue-500/20 text-blue-400"
            />
          )}
          {durationLabel != null && (
            <FilterPill
              label={durationLabel}
              onRemove={(): void => {
                setF('minDuration', 0);
                setF('maxDuration', 0);
              }}
              colorClass="bg-[var(--flux-warning)]/10 border-[var(--flux-warning)]/20 text-[var(--flux-warning)]"
            />
          )}
          {projectLabel != null && (
            <FilterPill
              label={`app: ${projectLabel}`}
              onRemove={(): void => setF('projectId', '')}
              colorClass="bg-purple-500/10 border-purple-500/20 text-purple-400"
            />
          )}
          {similarTo && (
            <FilterPill
              label={`Similar to: ${shortEntry(similarTo.entry ?? '')}`}
              onRemove={(): void => setSimilarTo(null)}
              colorClass="bg-purple-500/10 border-purple-500/20 text-purple-400"
            />
          )}
        </div>
      )}

      {/* ── 4. Intelligence Banner ─────────────────────────────────── */}
      <IntelligenceBanner executions={executions} />

      {/* ── 5. Result Count ───────────────────────────────────────── */}
      {!isLoading && (
        <div className="flex items-center gap-5 text-[11px] font-mono pb-1">
          <span className="text-muted-foreground/50">
            <span className="text-foreground font-semibold text-sm">{executions.length}</span>
            {similarTo ? ' similar traces' : ' results'}
            {totalResults !== executions.length && (
              <span className="text-muted-foreground/30 ml-0.5">(of {totalResults})</span>
            )}
          </span>
          {errorCount > 0 && (
            <span className="flex items-center gap-1 text-[var(--flux-error)]">
              <XCircle className="h-3 w-3" />
              {errorCount} errors
            </span>
          )}
          {slowCount > 0 && (
            <span className="flex items-center gap-1 text-[var(--flux-warning)]">
              <Clock className="h-3 w-3" />
              {slowCount} slow
            </span>
          )}
          {isFetching && (
            <span className="ml-auto text-muted-foreground/25 flex items-center gap-1">
              <Activity className="h-3 w-3 animate-pulse" />
              Refreshing…
            </span>
          )}
        </div>
      )}

      {/* ── 6. Results ────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex flex-col gap-2">
          {(Array(5) as unknown[]).map((_, i): import('react/jsx-runtime').JSX.Element => (
            <div
              key={i}
              className="h-12 rounded-xl bg-[#111318] border border-white/5 animate-pulse"
              style={{ opacity: 1 - i * 0.15 }}
            />
          ))}
        </div>
      ) : executions.length === 0 ? (
        <EmptyState hasFilters={hasFilters} onClear={clearAll} />
      ) : (
        <div className="flex flex-col gap-2">
          {executions.map((exec): import('react/jsx-runtime').JSX.Element => (
            <div key={exec.executionId ?? ''} className="group relative">
              <TraceCard
                rootNode={{ exec: exec, children: [] }}
                ongoingErrorPatterns={[]}
                onFindSimilar={(): void => setSimilarTo(exec)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
